// Produkty sprzedawcy partnerskiego na stronę główną.
//
// ŚWIADOMIE bez listy ID ofert: identyfikatory ofert zmieniają się przy każdym
// przescrapowaniu źródła (oferta znika -> is_valid=0, wraca z nowym id), a poza tym
// różnią się między lokalną bazą a produkcją. Lista ID zamieniłaby karuzelę
// w cichą awarię: puste miejsce albo produkt, którego już nie ma w sprzedaży.
// Dlatego pytamy backend o oferty PO NAZWIE SPRZEDAWCY (filtr `sellers` istnieje
// już na endpointach panels/ inverters/ magazines/).

import { apiFetch } from './apiFetch'
const apiUrl = process.env.REACT_APP_API_URL || '/api/'

export const PARTNER_SELLER = 'Twoja fotowoltaika'

/**
 * Sprzedawcy, których oferty trafiają do „Polecanych".
 *
 * NAZWY MUSZĄ BYĆ DOKŁADNIE TAKIE JAK W BAZIE — filtr `sellers` dopasowuje wprost,
 * bez normalizacji. „Super-Energy" z myślnikiem zwracało 0 ofert we wszystkich
 * trzech kategoriach; w katalogu ten sklep nazywa się „Super Energy" ze spacją
 * (50 ofert). Zanim dopiszesz kolejnego, sprawdź nazwę zapytaniem do API.
 */
export const SPRZEDAWCY_POLECANI = [PARTNER_SELLER, 'Super Energy']

// productType musi być zgodny z trasami w App.js (/panel/:id, /inwerter/:id, /magazyn/:id)
const CATEGORIES = [
  { productType: 'panel', endpoint: 'panels' },
  { productType: 'inwerter', endpoint: 'inverters' },
  { productType: 'magazyn', endpoint: 'magazines' },
]

const getJson = async (path) => {
  try {
    const res = await apiFetch(`${apiUrl}${path}`)
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

// Akcesoria i pozycje, które nie są produktem głównym — nie chcemy ich na stronie
// głównej jako "polecane" (podstawy, okablowanie, przedłużenia gwarancji itp.).
const JUNK_RE = /podstaw|okablowani|skrzynk|przy.{1,2}czeniow|junction|\bkabel\b|przew.d|wspornik|uchwyt|akcesor|zestaw monta|gwarancj/i

// Ta sama oferta bywa w bazie kilka razy (różne id, ten sam produkt).
// Klucz deduplikacji: model_id jeśli jest, inaczej znormalizowana nazwa.
const dedupeKey = (p) =>
  p.model_id != null ? `m:${p.model_id}` : `n:${(p.name || '').trim().toLowerCase()}`

const usable = (p) =>
  p && p.id != null && p.name && !JUNK_RE.test(p.name) && p.price != null && p.price > 0

const fetchCategory = async ({ productType, endpoint }, limit, seller) => {
  const q = new URLSearchParams({
    sellers: seller,
    available_only: 'true',
    order_by_price: 'asc',
    limit: String(limit),
  })
  const data = await getJson(`${endpoint}/?${q.toString()}`)
  const items = data?.items || []

  const seen = new Set()
  const out = []
  for (const p of items) {
    if (!usable(p)) continue
    const key = dedupeKey(p)
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ productType, product: p })
  }

  // Oferty powiązane z modelem kanonicznym (model_id) mają pierwszeństwo. Nie jest to
  // kosmetyka: warstwa modeli odsiewa to, czego nie da się rozpoznać — akcesoria,
  // moduły sterujące i pozycje wpadające do złej kategorii (w katalogu falowników
  // siedzi m.in. moduł PV — pułapka #4 z 00_KONTRAKT.md). Sortujemy po cenie rosnąco,
  // więc bez tego filtra na stronie głównej lądują właśnie te najtańsze śmieci.
  const modeled = out.filter((x) => x.product.model_id != null)
  const ranked = modeled.length ? modeled : out

  // Ze zdjęciem na początku — karta bez zdjęcia wygląda na stronie głównej źle,
  // ale nie odrzucamy jej całkiem (lepsza niż puste miejsce w karuzeli).
  return [...ranked.filter((x) => x.product.img_url), ...ranked.filter((x) => !x.product.img_url)]
}

/**
 * Zwraca do `count` produktów sprzedawcy partnerskiego, przeplatanych między
 * kategoriami (moduł, falownik, magazyn, moduł, ...). Gdy któraś kategoria ma
 * mniej ofert, brakujące miejsca uzupełniają pozostałe kategorie.
 * Zwraca [] gdy API nie odpowiada — wywołujący ma wtedy nie renderować sekcji.
 */
export const fetchPartnerProducts = async (count = 6, sprzedawcy = SPRZEDAWCY_POLECANI) => {
  // Kategorie × sprzedawcy, spłaszczone do jednej listy koszyków. Przeplatanie niżej
  // bierze po jednym z każdego koszyka, więc obaj sprzedawcy trafiają do karuzeli
  // nawet wtedy, gdy jeden ma znacznie więcej ofert.
  const koszyki = await Promise.all(
    sprzedawcy.flatMap((sp) => CATEGORIES.map((c) => fetchCategory(c, 60, sp)))
  )
  const perCategory = koszyki

  const out = []
  const cursors = perCategory.map(() => 0)
  let progress = true
  while (out.length < count && progress) {
    progress = false
    for (let i = 0; i < perCategory.length && out.length < count; i += 1) {
      const list = perCategory[i]
      if (cursors[i] < list.length) {
        out.push(list[cursors[i]])
        cursors[i] += 1
        progress = true
      }
    }
  }
  return out
}
